import { useCallback } from 'react';
import OriginTracing from '@atlassiansox/origin-tracing';
import { useInvisibleRecaptcha } from '@atlassian/google-recaptcha';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { performPostRequest } from '@atlassian/jira-fetch';
import { useFlagService, toFlagId } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireTrackAnalytics,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId, useEnvironment } from '@atlassian/jira-tenant-context-controller';
import { INVITED_STATUS, USER_EXISTS_STATUS, INVITE_USERS_ENDPOINT } from './constants';
import messages from './messages';
import type {
	InviteUsersApiResponse,
	InviteUserRequest,
	InviteUserResponse,
	InviteUsersApiRequest,
} from './types';
import { getJiraSoftwareAri, getRecaptchaSiteKey, transformInviteResult } from './utils';

export const ERROR_FLAG_ID = toFlagId('UNSUCCESSFUL_INVITE');

export const postInviteUsers = async ({
	resources,
	users,
	continueUrl,
	recaptchaToken,
}: InviteUsersApiRequest): Promise<InviteUsersApiResponse> =>
	performPostRequest(INVITE_USERS_ENDPOINT, {
		headers: {
			'Content-Type': 'application/json',
			...(recaptchaToken ? { 'G-Recaptcha-Token': recaptchaToken } : {}),
		},
		body: JSON.stringify({
			users,
			resources,
			continueUrl,
		}),
	});

export const useInviteUserService = ({ analyticsSource }: { analyticsSource: string }) => {
	const environment = useEnvironment();
	const cloudId = useCloudId();
	const { showFlag } = useFlagService();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { getRecaptchaToken } = useInvisibleRecaptcha({
		siteKey: getRecaptchaSiteKey(environment),
		displayBadge: false,
		analyticsChannel: 'assigneeView',
		analyticsSource,
	});

	return useCallback(
		async ({ email, issueKey }: InviteUserRequest): Promise<InviteUserResponse> => {
			try {
				const jiraAri = getJiraSoftwareAri(cloudId);
				const recaptchaToken = await getRecaptchaToken?.('v1_invitations_invite').catch(
					// If token generation fails we want to just continue with the
					// request without a recaptcha token (the lib will fire an event)
					() => undefined,
				);

				const atlOrigin = new OriginTracing({ product: 'jira' });
				const continueUrl = atlOrigin.addToUrl(`${window.location.origin}/browse/${issueKey}`);
				const invitedUsers = await postInviteUsers({
					resources: [jiraAri],
					users: [
						{
							email,
						},
					],
					continueUrl,
					recaptchaToken,
				});

				const invitedUser = invitedUsers[0];
				const inviteResult = invitedUser.results[jiraAri];

				if (inviteResult === INVITED_STATUS) {
					fireTrackAnalytics(createAnalyticsEvent({}), 'user invited', invitedUser.id, {
						integration: 'assigneeViewField',
						...atlOrigin.toAnalyticsAttributes({ hasGeneratedId: true }),
					});
				}

				if (inviteResult === USER_EXISTS_STATUS) {
					fireOperationalAnalytics(createAnalyticsEvent({}), 'userInvite alreadyExists');
				}

				return transformInviteResult({
					userId: invitedUser.id,
					result: inviteResult,
					errorReason: invitedUser.errorReasons[jiraAri],
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInviteUserService',
						packageName: 'jiraIssueViewServices',
						// eslint-disable-next-line jira/js/team-name-enforcer-for-error-analytics
						teamName: 'jlove-makkuro',
					},
					error,
				});

				showFlag({
					id: ERROR_FLAG_ID,
					type: 'error',
					title: formatMessage(messages.errorFlagTitle),
					description: formatMessage(messages.errorFlagDescription),
					isAutoDismiss: true,
				});

				return {
					userId: null,
					isInviteSuccessful: false,
				};
			}
		},
		[cloudId, createAnalyticsEvent, formatMessage, getRecaptchaToken, showFlag],
	);
};
